import React, { useState, useRef, useEffect } from "react";
import { Offcanvas, Form, Button, Row, Col, Alert, } from "react-bootstrap";
import { Formik, Form as FormikForm, FieldArray, FormikErrors, FormikTouched } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { IoIosRemoveCircleOutline } from "react-icons/io";
import AppConstants from "../../AppConstants";

// Validation schema
const PlanSchema = Yup.object().shape({
    name: Yup.string().required("Plan name is required"),
    billing: Yup.string().required("Billing cycle is required"),
    features: Yup.array().of(Yup.string().required("Feature is required")),
    pricingTiers: Yup.array().of(
        Yup.object().shape({
            users: Yup.number().required("Number of users is required"),
            amount: Yup.number().required("Amount is required")
        })
    )
});

interface PricingTier {
    users: number;
    amount: number;
    stripePriceId?: string;
}

interface Plan {
    id: number;
    name: string;
    billing: string;
    features: string[];
    pricingTiers: PricingTier[];
}

interface PlanDetails {
    _id: string;
    details: string[];
    createdAt: string;
    updatedAt: string;
}

interface EditPlanFormProps {
    show: boolean;
    onHide: () => void;
    plan: Plan;
    onUpdate: (plan: Plan) => void;
    onPlanDetailsUpdate?: (details: string[]) => void; // Add this prop
}

const EditPlanForm: React.FC<EditPlanFormProps> = ({
    show,
    onHide,
    plan,
    onUpdate,
    onPlanDetailsUpdate // Add this prop
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [debugInfo, setDebugInfo] = useState<string>("");
    const [planDetails, setPlanDetails] = useState<string[]>([]);
    const [planDetailsId, setPlanDetailsId] = useState<string>("");
    const [isLoadingPlanDetails, setIsLoadingPlanDetails] = useState(false);
    const [isSavingPlanDetails, setIsSavingPlanDetails] = useState(false);

    const [planDetailsModified, setPlanDetailsModified] = useState(false);

    // Track original values and changed tiers
    const originalValues = useRef<Plan>(JSON.parse(JSON.stringify(plan)));
    const [changedTiers, setChangedTiers] = useState<Set<number>>(new Set());

    // Helper function to validate Stripe price ID format
    const isValidStripePriceId = (priceId: string): boolean => {
        return Boolean(priceId && priceId.startsWith('price_') && priceId.length >= 14);
    };

    // Helper function to add delay between requests
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // Helper function to decode JWT and get user ID
    const getUserIdFromToken = (token: string): string | null => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.userId || null;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    };

    // Get authentication token
    const getAuthToken = (): string | null => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            toast.error("Authentication token not found. Please login again.");
            return null;
        }
        return token;
    };

    // Fetch plan details
    const fetchPlanDetails = async () => {
        try {
            setIsLoadingPlanDetails(true);
            const token = getAuthToken();
            if (!token) return;

            const response = await fetch(AppConstants.ADMIN_GET_PLAN_DETAILS_API, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData.message || `HTTP ${response.status}`);
            }

            if (responseData.success && responseData.data) {
                const details = responseData.data.details || [];
                setPlanDetails(details);
                setPlanDetailsId(responseData.data._id || "");
            } else {
                // If no plan details exist, initialize with empty array
                setPlanDetails([]);
                setPlanDetailsId("");
            }

        } catch (error) {
            console.error('Error fetching plan details:', error);
            const errorMessage = error instanceof Error ? error.message : "Failed to fetch plan details";
            toast.error(`Error loading plan details: ${errorMessage}`);
            setPlanDetails([]);
        } finally {
            setIsLoadingPlanDetails(false);
        }
    };

    // Save plan details
    // In EditPlanForm component, update the savePlanDetails function
    const savePlanDetails = async () => {
        // Validation: Check if any detail is empty or only whitespace
        const hasEmptyDetails = planDetails.some(detail => detail.trim() === "");

        if (hasEmptyDetails) {
            toast.error("Please fill in all plan details or remove empty fields.");
            return;
        }

        try {
            setIsSavingPlanDetails(true);
            const token = getAuthToken();
            if (!token) return;

            // Filter out empty details (additional safety check)
            const filteredDetails = planDetails.filter(detail => detail.trim() !== "");

            if (filteredDetails.length === 0) {
                toast.warning("Please add at least one plan detail.");
                return;
            }

            const requestBody = {
                details: filteredDetails
            };

            const response = await fetch(AppConstants.ADMIN_POST_PLAN_DETAILS_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData.message || `HTTP ${response.status}`);
            }

            if (responseData.success) {
                toast.success("Plan details saved successfully!");
                setPlanDetailsId(responseData.data._id || "");
                setPlanDetailsModified(false); // Reset modification flag

                // Update the parent component immediately
                if (onPlanDetailsUpdate) {
                    onPlanDetailsUpdate(filteredDetails);
                }

                // Close the modal after successful save
                onHide();

            } else {
                throw new Error(responseData.message || 'Failed to save plan details');
            }

        } catch (error) {
            console.error('Error saving plan details:', error);
            const errorMessage = error instanceof Error ? error.message : "Failed to save plan details";
            toast.error(`Error saving plan details: ${errorMessage}`);
        } finally {
            setIsSavingPlanDetails(false);
        }
    };

    // Track which tiers have been modified
    const handleAmountChange = (
        index: number,
        event: React.ChangeEvent<HTMLInputElement>,
        handleChange: (e: React.ChangeEvent<any>) => void
    ) => {
        const newAmount = parseFloat(event.target.value);
        const originalAmount = originalValues.current.pricingTiers[index]?.amount;

        // Update the changed tiers set
        setChangedTiers(prev => {
            const newSet = new Set(prev);
            if (newAmount !== originalAmount && !isNaN(newAmount)) {
                newSet.add(index);
            } else {
                newSet.delete(index);
            }
            return newSet;
        });

        // Call the original handleChange
        handleChange(event);
    };

    // Get only the changed tiers that need to be updated
    const getChangedTiers = (updatedPlan: Plan): PricingTier[] => {
        const tiersToUpdate: PricingTier[] = [];

        changedTiers.forEach(index => {
            const tier = updatedPlan.pricingTiers[index];
            const originalTier = originalValues.current.pricingTiers[index];

            // Only include if amount actually changed and has valid price ID
            if (tier &&
                tier.amount !== originalTier?.amount &&
                tier.stripePriceId &&
                isValidStripePriceId(tier.stripePriceId)) {
                tiersToUpdate.push(tier);
            }
        });

        return tiersToUpdate;
    };

    const handleUpdatePricing = async (updatedPlan: Plan) => {
        try {
            setIsSubmitting(true);
            setDebugInfo("");

            const token = getAuthToken();
            if (!token) return;

            const currentUserId = getUserIdFromToken(token);
            if (!currentUserId) {
                toast.error("Unable to identify current user. Please login again.");
                return;
            }

            const tiersToUpdate = getChangedTiers(updatedPlan);

            if (tiersToUpdate.length === 0) {
                toast.warning("No changes detected in pricing System: amounts.");
                return;
            }

            const debugData = {
                totalTiers: updatedPlan.pricingTiers.length,
                changedTiers: changedTiers.size,
                tiersToUpdate: tiersToUpdate.length,
                changedIndexes: Array.from(changedTiers),
                updateDetails: tiersToUpdate.map(tier => ({
                    users: tier.users,
                    newAmount: tier.amount,
                    originalAmount: originalValues.current.pricingTiers.find(t => t.users === tier.users)?.amount,
                    priceId: tier.stripePriceId
                }))
            };

            setDebugInfo(JSON.stringify(debugData, null, 2));

            // Prepare single API call with all updates
            const requestBody = {
                updates: tiersToUpdate.map(tier => ({
                    licenses: tier.users,
                    priceId: tier.stripePriceId,
                    unitAmount: tier.amount,
                    updatedBy: currentUserId
                }))
            };

            const response = await fetch(AppConstants.ADMIN_EDIT_PRICES_API, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData.message || `HTTP ${response.status}`);
            }

            if (!responseData.success) {
                throw new Error(responseData.message || 'Update failed');
            }

            // Handle successful update
            toast.success(`${tiersToUpdate.length} pricing tier(s) updated successfully!`);

            setChangedTiers(new Set());
            originalValues.current = JSON.parse(JSON.stringify(updatedPlan));

            const updatedPlanData = {
                ...updatedPlan,
                features: plan.features
            };
            onUpdate(updatedPlanData);

        } catch (error) {
            console.error('Error updating pricing:', error);
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            toast.error(`Error updating pricing: ${errorMessage}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper function to get error message for pricing tier
    const getPricingTierError = (
        errors: FormikErrors<Plan>,
        touched: FormikTouched<Plan>,
        index: number
    ): string | undefined => {
        const tierError = errors.pricingTiers?.[index];
        const tierTouched = touched.pricingTiers?.[index];

        if (typeof tierError === 'object' && tierError !== null && 'amount' in tierError && tierTouched && typeof tierTouched === 'object' && 'amount' in tierTouched) {
            return tierError.amount;
        }
        return undefined;
    };

    // Plan details handlers
    const addPlanDetail = () => {
        setPlanDetails([...planDetails, ""]);
        setPlanDetailsModified(true);
    };

    const removePlanDetail = (index: number) => {
        const newDetails = planDetails.filter((_, i) => i !== index);
        setPlanDetails(newDetails);
        setPlanDetailsModified(true); // Mark as modified when user removes field
    };

    const updatePlanDetail = (index: number, value: string) => {
        const newDetails = [...planDetails];
        newDetails[index] = value;
        setPlanDetails(newDetails);
        setPlanDetailsModified(true); // Mark as modified when user types
    };

    // Reset tracking when form is opened/closed
    useEffect(() => {
        if (show) {
            originalValues.current = JSON.parse(JSON.stringify(plan));
            setChangedTiers(new Set());
            setDebugInfo("");
            setPlanDetailsModified(false); // Reset modification flag
            fetchPlanDetails(); // Fetch plan details when modal opens
        }
    }, [show, plan]);

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            className="edit-plan-canvas px-3"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title className="gilroy-semibold text-light" style={{ fontSize: '24px' }}>
                    Edit Plan
                </Offcanvas.Title>
            </Offcanvas.Header>
            <hr className="my-0" />

            <Offcanvas.Body>
                {debugInfo && (
                    <Alert variant="info" className="mb-3">
                        <small>
                            <strong>Debug Info:</strong>
                            <pre style={{ fontSize: '11px', marginTop: '8px' }}>{debugInfo}</pre>
                        </small>
                    </Alert>
                )}

                <Formik
                    initialValues={plan}
                    validationSchema={PlanSchema}
                    onSubmit={handleUpdatePricing}
                    enableReinitialize={true}
                >
                    {({ values, errors, touched, handleChange, handleBlur }) => (
                        <FormikForm>
                            <Form.Group className="mb-3">
                                <Form.Label>Plan Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={values.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={!!errors.name && touched.name}
                                    disabled // Make it read-only as we're only updating pricing
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <h4 className="text-light">Amount</h4>
                                <div className="pricing-tiers">
                                    {values.pricingTiers.map((tier, index) => (
                                        <React.Fragment key={index}>
                                            {index % 2 === 0 && (
                                                <Row className="mb-3">
                                                    <Col md={6}>
                                                        <Form.Label>
                                                            {tier.users} Users
                                                            {changedTiers.has(index) && (
                                                                <span className="badge bg-primary text-white ms-2">Modified</span>
                                                            )}
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            name={`pricingTiers.${index}.amount`}
                                                            placeholder="Enter Amount"
                                                            value={values.pricingTiers[index].amount}
                                                            onChange={(e) => handleAmountChange(index, e as React.ChangeEvent<HTMLInputElement>, handleChange)}
                                                            isInvalid={!!getPricingTierError(errors, touched, index)}
                                                            className={changedTiers.has(index) ? 'border-warning' : ''}
                                                            min="1"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {getPricingTierError(errors, touched, index)}
                                                        </Form.Control.Feedback>
                                                    </Col>
                                                    {index + 1 < values.pricingTiers.length && (
                                                        <Col md={6}>
                                                            <Form.Label>
                                                                {values.pricingTiers[index + 1].users} Users
                                                                {changedTiers.has(index + 1) && (
                                                                    <span className="badge bg-primary text-white ms-2">Modified</span>
                                                                )}
                                                            </Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                name={`pricingTiers.${index + 1}.amount`}
                                                                placeholder="Enter Amount"
                                                                value={values.pricingTiers[index + 1].amount}
                                                                onChange={(e) => handleAmountChange(index + 1, e as React.ChangeEvent<HTMLInputElement>, handleChange)}
                                                                isInvalid={!!getPricingTierError(errors, touched, index + 1)}
                                                                className={changedTiers.has(index + 1) ? 'border-warning' : ''}
                                                                min="1"
                                                            />
                                                            <Form.Control.Feedback type="invalid">
                                                                {getPricingTierError(errors, touched, index + 1)}
                                                            </Form.Control.Feedback>
                                                        </Col>
                                                    )}
                                                </Row>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </Form.Group>

                            <div className="d-grid gap-2 my-4">
                                <Button
                                    variant="success"
                                    type="submit"
                                    className="text-white gilroy-semibold"
                                    disabled={isSubmitting || changedTiers.size === 0}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Updating {changedTiers.size} Tier(s)...
                                        </>
                                    ) : (
                                        changedTiers.size > 0
                                            // ? `Update ${changedTiers.size} Changed Tier(s)`
                                            ? `Update`
                                            : "Update"
                                    )}
                                </Button>
                            </div>

                            <hr />



                            {/* Plan Details Section */}
                            <Form.Group className="mb-4">
                                <div className="d-flex justify-content-between mb-3">
                                    <Form.Label className="gilroy-semibold" style={{ fontSize: '20px' }}>
                                        Plan Details

                                    </Form.Label>
                                    <Button
                                        variant="success"
                                        onClick={addPlanDetail}
                                        className="text-white gilroy-semibold"
                                        disabled={isLoadingPlanDetails}
                                    >
                                        <i className="bi bi-plus"></i> Add Detail
                                    </Button>
                                </div>

                                {isLoadingPlanDetails ? (
                                    <div className="text-center py-3">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Loading plan details...
                                    </div>
                                ) : (
                                    <div className="plan-details">
                                        {planDetails.map((detail: string, index: number) => (
                                            <div key={index} className="position-relative">
                                                <Form.Control
                                                    type="text"
                                                    value={detail}
                                                    onChange={(e) => updatePlanDetail(index, e.target.value)}
                                                    style={{ maxWidth: '90%' }}
                                                    className="mb-3"
                                                    placeholder="Enter plan detail"
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    right: 0,
                                                    top: 10
                                                }}>
                                                    <IoIosRemoveCircleOutline
                                                        className="text-danger"
                                                        onClick={() => removePlanDetail(index)}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                </div>
                                            </div>
                                        ))}

                                        {planDetails.length > 0 && (
                                            <div className="d-grid gap-2 mt-3">
                                                <Button
                                                    variant="primary"
                                                    onClick={savePlanDetails}
                                                    className="text-white gilroy-semibold"
                                                    disabled={
                                                        isSavingPlanDetails ||
                                                        !planDetailsModified ||
                                                        planDetails.some(detail => detail.trim() === "") ||
                                                        planDetails.length === 0
                                                    }
                                                >
                                                    {isSavingPlanDetails ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Saving Plan Details...
                                                        </>
                                                    ) : (
                                                        'Save Plan Details'
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Form.Group>


                        </FormikForm>
                    )}
                </Formik>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default EditPlanForm;